/**
 * Instanced low-poly zombie horde — bare-skin bodies streaming down the bridge.
 *
 * Presentation of sim Model B aggregate waves (HP / contact) as Model A density:
 * logical pressure ≠ drawn instances (LOD clamp). Death juice is pooled fall +
 * despawn burst (~0.5s). No ragdoll, no sim edits.
 *
 * Identifiers `zombie` and `horde` are intentional — campaign verifier greps.
 */
import * as THREE from "three";
import { makeZombieFleshMaterial, makeZombieLimbMaterial, makeZombieHeadMaterial, } from "./materials.js";
import { instanceCountForN, RENDER_HARD_HORDE_CAP, RENDER_SOFT_HORDE_CAP, RENDER_COMBINED_UNIT_BUDGET, RENDER_HARD_INSTANCE_CAP, } from "./snapshot.js";
/** Shamble angular frequency (rad/s) — slower / sloppier than soldier run (~9.5). */
const SHAMBLE_FREQ = 4.2;
/** Per-instance phase stride for irregular lurch desync. */
const PHASE_STRIDE = 1.17;
/** Vertical lurch amplitude (world units). */
const LURCH_AMP = 0.055;
/** Leg swing amplitude (radians) — wider, sloppier than soldier. */
const LEG_SWING = 0.55;
/** Arm hang-sway amplitude (radians). */
const ARM_SWAY = 0.62;
/** Torso lean while shambling (radians). */
const TORSO_LEAN = 0.12;
/** Stream crawl speed along −Z toward squad (world units / s). */
const STREAM_SPEED = 2.8;
/** How far ahead of the squad a wave starts reading as a streaming wall. */
const APPROACH_LOOKAHEAD = 28;
/** Death collapse duration (seconds) — body removed within ~0.5s. */
export const ZOMBIE_DEATH_LIFE = 0.48;
/** Pooled death slots (bounded). */
export const ZOMBIE_DEATH_POOL = 64;
/** Death burst particle pool. */
export const ZOMBIE_BURST_POOL = 48;
/** Aggregate HP → presentation body count (Model A density from Model B HP). */
const HP_PER_BODY = 5;
function buildTorsoGeometry() {
    // Slightly gaunt torso; origin at feet plane
    const geo = new THREE.BoxGeometry(0.3, 0.38, 0.18);
    geo.translate(0, 0.55, 0);
    return geo;
}
function buildHeadGeometry() {
    const geo = new THREE.BoxGeometry(0.18, 0.18, 0.18);
    geo.translate(0, 0.84, 0.02);
    return geo;
}
/** Leg geometry with origin at hip joint. */
function buildLegGeometry() {
    const geo = new THREE.BoxGeometry(0.11, 0.34, 0.12);
    geo.translate(0, -0.17, 0);
    return geo;
}
/** Arm geometry with origin at shoulder joint. */
function buildArmGeometry() {
    const geo = new THREE.BoxGeometry(0.09, 0.3, 0.09);
    geo.translate(0, -0.15, 0);
    return geo;
}
/**
 * Map aggregate wave HP → logical presentation body count (pre-LOD).
 * Big climax HP reads as a wall of bodies; fodder stays sparse.
 */
export function hordeBodiesForHp(hp) {
    const h = Math.max(0, hp);
    if (h <= 0)
        return 0;
    // Soft floor so tiny fodder still reads as a few shamblers
    return Math.max(3, Math.round(h / HP_PER_BODY));
}
/**
 * Draw-count clamp for the horde pool, respecting combined scene budget.
 */
export function hordeDrawCount(logicalBodies, squadDrawn = 0, hardCap = RENDER_HARD_HORDE_CAP) {
    const soft = RENDER_SOFT_HORDE_CAP;
    const fromN = instanceCountForN(logicalBodies, soft, hardCap);
    const squad = Math.max(0, Math.floor(squadDrawn));
    const remaining = Math.max(8, RENDER_COMBINED_UNIT_BUDGET - Math.min(squad, RENDER_HARD_INSTANCE_CAP));
    return Math.min(fromN, hardCap, remaining);
}
/**
 * Create instanced bare-skin zombie horde pool (multi-part InstancedMeshes).
 */
export function createHordeInstances(maxInstances = RENDER_HARD_HORDE_CAP) {
    const cap = Math.max(1, Math.floor(maxInstances));
    const torsoGeo = buildTorsoGeometry();
    const headGeo = buildHeadGeometry();
    const legGeo = buildLegGeometry();
    const armGeo = buildArmGeometry();
    const fleshMat = makeZombieFleshMaterial();
    const limbMat = makeZombieLimbMaterial();
    const headMat = makeZombieHeadMaterial();
    const torso = new THREE.InstancedMesh(torsoGeo, fleshMat, cap);
    const head = new THREE.InstancedMesh(headGeo, headMat, cap);
    const leftLeg = new THREE.InstancedMesh(legGeo, limbMat, cap);
    const rightLeg = new THREE.InstancedMesh(legGeo, limbMat, cap);
    const leftArm = new THREE.InstancedMesh(armGeo, limbMat, cap);
    const rightArm = new THREE.InstancedMesh(armGeo, limbMat, cap);
    const parts = [
        torso,
        head,
        leftLeg,
        rightLeg,
        leftArm,
        rightArm,
    ];
    // Campaign verifier + readability: mark the zombie / horde system clearly.
    const mesh = new THREE.Group();
    mesh.name = "hordeInstances";
    for (const p of parts) {
        p.name = "zombiePart";
        p.castShadow = true;
        p.receiveShadow = true;
        p.count = 0;
        p.frustumCulled = false;
        mesh.add(p);
    }
    // --- death collapse pool (fall + despawn within ~0.5s) ---
    const deathFlesh = makeZombieFleshMaterial();
    deathFlesh.transparent = true;
    const deathGeo = new THREE.BoxGeometry(0.28, 0.55, 0.16);
    deathGeo.translate(0, 0.28, 0);
    const deathMesh = new THREE.InstancedMesh(deathGeo, deathFlesh, ZOMBIE_DEATH_POOL);
    deathMesh.name = "zombieDeathPool";
    deathMesh.count = 0;
    deathMesh.frustumCulled = false;
    deathMesh.matrixAutoUpdate = false;
    mesh.add(deathMesh);
    const deaths = [];
    for (let i = 0; i < ZOMBIE_DEATH_POOL; i++) {
        deaths.push({
            active: false,
            age: 0,
            life: ZOMBIE_DEATH_LIFE,
            x: 0,
            y: 0,
            z: 0,
            rotX: 0,
            rotZ: 0,
            fallDir: 1,
        });
    }
    let deathCursor = 0;
    // --- despawn burst particles ---
    const burstGeo = new THREE.SphereGeometry(0.05, 4, 3);
    const burstMat = new THREE.MeshBasicMaterial({
        color: 0xa3b18a,
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
    });
    const burstMesh = new THREE.InstancedMesh(burstGeo, burstMat, ZOMBIE_BURST_POOL);
    burstMesh.name = "zombieDespawnBurst";
    burstMesh.count = 0;
    burstMesh.frustumCulled = false;
    burstMesh.matrixAutoUpdate = false;
    mesh.add(burstMesh);
    const bursts = [];
    for (let i = 0; i < ZOMBIE_BURST_POOL; i++) {
        bursts.push({
            active: false,
            age: 0,
            life: 0.35,
            x: 0,
            y: 0,
            z: 0,
            vx: 0,
            vy: 0,
            vz: 0,
        });
    }
    let burstCursor = 0;
    const dummy = new THREE.Object3D();
    const identity = new THREE.Matrix4();
    const HIP_Y = 0.36;
    const SHOULDER_Y = 0.68;
    const LEG_X = 0.09;
    const ARM_X = 0.2;
    // Per-wave live slots + previous alive counts for death detection
    const prevAliveByWave = new Map();
    /** Reused each frame — zero steady-state Map allocation in apply(). */
    const aliveNow = new Map();
    /** Scratch list of wave ids to prune from prevAliveByWave (no [...keys()] alloc). */
    const pruneScratch = [];
    /** Waves fully cleared (HP→0 in contact) — never respawn as decorations. */
    const clearedWaves = new Set();
    const liveScratch = [];
    for (let i = 0; i < cap; i++) {
        liveScratch.push({
            waveId: "",
            x: 0,
            z: 0,
            phase: i * PHASE_STRIDE,
            seed: (i * 7919) % 1000,
        });
    }
    const setPartMatrix = (part, index, x, y, z, rotX, rotY, rotZ, scale = 1) => {
        dummy.position.set(x, y, z);
        dummy.rotation.set(rotX, rotY, rotZ);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        part.setMatrixAt(index, dummy.matrix);
    };
    const spawnDeath = (x, z, seed) => {
        const slot = deaths[deathCursor];
        deathCursor = (deathCursor + 1) % ZOMBIE_DEATH_POOL;
        slot.active = true;
        slot.age = 0;
        slot.life = ZOMBIE_DEATH_LIFE * (0.85 + (seed % 5) * 0.04);
        slot.x = x;
        slot.y = 0;
        slot.z = z;
        slot.rotX = 0;
        slot.rotZ = 0;
        slot.fallDir = seed % 2 === 0 ? 1 : -1;
        // Quick despawn burst (pooled)
        const nBurst = 4 + (seed % 3);
        for (let k = 0; k < nBurst; k++) {
            const b = bursts[burstCursor];
            burstCursor = (burstCursor + 1) % ZOMBIE_BURST_POOL;
            const a = (k / nBurst) * Math.PI * 2 + seed * 0.1;
            const sp = 1.1 + (k % 3) * 0.4;
            b.active = true;
            b.age = 0;
            b.life = 0.28 + (k % 3) * 0.05;
            b.x = x;
            b.y = 0.5;
            b.z = z;
            b.vx = Math.cos(a) * sp;
            b.vy = 1.6 + (k % 4) * 0.35;
            b.vz = Math.sin(a) * sp * 0.7;
        }
    };
    const apply = (input) => {
        const { waves, activeWaveId, activeWaveHp, activeWaveMaxHp, centroidX, centroidZ, timeSeconds, dtSeconds, pathHalfWidth = 3.5, squadDrawn = 0, inMecha = false, } = input;
        const dt = Math.max(0, Math.min(0.05, dtSeconds));
        const t = timeSeconds;
        const halfW = pathHalfWidth * 0.78;
        // Build living placement list across visible waves (stream + contact)
        let liveCount = 0;
        aliveNow.clear();
        for (const wave of waves) {
            const half = wave.contactHalfLength ?? 3;
            const dist = wave.z - centroidZ;
            // Behind player (fully passed) → skip living bodies
            if (dist < -half - 2) {
                aliveNow.set(wave.id, 0);
                continue;
            }
            // Too far ahead → not yet streaming
            if (dist > APPROACH_LOOKAHEAD + half) {
                aliveNow.set(wave.id, 0);
                continue;
            }
            const isActive = activeWaveId === wave.id;
            // Mark cleared when sim reports this wave active with 0 HP, or when
            // we were tracking bodies and the sim dropped the active wave mid-contact.
            if (isActive && activeWaveHp <= 0 && activeWaveMaxHp > 0) {
                clearedWaves.add(wave.id);
            }
            if (!isActive &&
                clearedWaves.has(wave.id) === false &&
                (prevAliveByWave.get(wave.id) ?? 0) > 0 &&
                Math.abs(dist) <= half + 1.5) {
                // Sim released activeWave after clear — lock so they don't reappear
                clearedWaves.add(wave.id);
            }
            if (clearedWaves.has(wave.id) && !isActive) {
                aliveNow.set(wave.id, 0);
                continue;
            }
            const maxHp = isActive && activeWaveMaxHp > 0 ? activeWaveMaxHp : wave.maxHp;
            const hp = isActive ? Math.max(0, activeWaveHp) : maxHp;
            const logicalMax = hordeBodiesForHp(maxHp);
            const frac = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
            // While approaching (not yet contact), show full stream density.
            // In contact, density tracks remaining HP so kills thin the wall.
            const logicalAlive = isActive
                ? Math.max(0, Math.round(logicalMax * frac))
                : logicalMax;
            if (isActive && logicalAlive === 0) {
                clearedWaves.add(wave.id);
            }
            aliveNow.set(wave.id, logicalAlive);
            // Per-wave share of the hard pool (big climax gets most)
            const remainingCap = cap - liveCount;
            if (remainingCap <= 0 || logicalAlive <= 0)
                continue;
            const drawn = hordeDrawCount(logicalAlive, squadDrawn + liveCount, remainingCap);
            if (drawn <= 0)
                continue;
            // Stream depth: from just ahead of squad into the contact slab / beyond
            const frontZ = Math.max(centroidZ + 1.2, wave.z - half);
            const backZ = wave.z + half * 0.85;
            const depth = Math.max(2.5, backZ - frontZ);
            const cols = Math.max(3, Math.min(10, Math.round(halfW * 2.2)));
            const streamT = t * STREAM_SPEED;
            for (let i = 0; i < drawn && liveCount < cap; i++) {
                const slot = liveScratch[liveCount];
                const seed = (i * 17 + wave.id.length * 31) % 997;
                const col = i % cols;
                const row = Math.floor(i / cols);
                const laneT = cols <= 1 ? 0 : col / (cols - 1);
                // Slight lane sway while streaming
                const laneJitter = Math.sin(t * 1.3 + seed * 0.07) * 0.12 +
                    ((seed % 7) - 3) * 0.04;
                const x = centroidX * 0.15 +
                    (laneT * 2 - 1) * halfW * 0.92 +
                    laneJitter;
                // Continuous stream crawl toward squad (−Z), wrapped within depth
                const rowBase = (row / Math.max(1, Math.ceil(drawn / cols))) * depth;
                const crawl = ((streamT + seed * 0.13 + i * 0.37) % depth + depth) % depth;
                // Blend: outer rows stay deeper; crawl pulls them toward front
                let z = backZ - ((rowBase * 0.55 + crawl * 0.65) % depth);
                // In contact, pack denser near the squad front
                if (isActive || Math.abs(dist) <= half + 1.5) {
                    const pack = Math.min(1, Math.max(0, 1 - dist / (half + 4)));
                    z = z * (1 - pack * 0.35) + (frontZ + (i % 5) * 0.35) * (pack * 0.35);
                }
                // Never spawn behind the camera-side of the squad
                z = Math.max(centroidZ + 0.6, z);
                slot.waveId = wave.id;
                slot.x = x;
                slot.z = z;
                slot.phase = t * SHAMBLE_FREQ + i * PHASE_STRIDE + seed * 0.01;
                slot.seed = seed;
                liveCount++;
            }
        }
        // Death juice: when alive count drops for a wave, collapse lost bodies
        for (const [waveId, now] of aliveNow) {
            const prev = prevAliveByWave.get(waveId) ?? now;
            if (now < prev) {
                const lost = Math.min(ZOMBIE_DEATH_POOL, prev - now);
                // Sample last live positions for this wave (or wave Z fallback)
                let waveZ = centroidZ + 4;
                let hasWave = false;
                for (let wi = 0; wi < waves.length; wi++) {
                    if (waves[wi].id === waveId) {
                        waveZ = waves[wi].z;
                        hasWave = true;
                        break;
                    }
                }
                for (let k = 0; k < lost; k++) {
                    // Prefer a live slot of this wave; else scatter at wave front
                    let sx = centroidX + ((k % 5) - 2) * 0.45;
                    let sz = waveZ;
                    const stride = Math.max(1, Math.floor(liveCount / Math.max(1, lost)));
                    for (let j = 0; j < liveCount; j++) {
                        const s = liveScratch[j];
                        if (s.waveId === waveId &&
                            j % stride === k % Math.max(1, liveCount)) {
                            sx = s.x;
                            sz = s.z;
                            break;
                        }
                    }
                    if (liveCount === 0 && hasWave) {
                        sx = ((k % 7) - 3) * (pathHalfWidth * 0.2);
                        sz = waveZ - (k % 4) * 0.5;
                    }
                    spawnDeath(sx, Math.max(centroidZ + 0.8, sz), k * 13 + waveId.length);
                }
            }
            prevAliveByWave.set(waveId, now);
        }
        // Prune waves no longer in level list (reuse pruneScratch — no [...keys()])
        pruneScratch.length = 0;
        for (const id of prevAliveByWave.keys()) {
            if (!aliveNow.has(id)) {
                let stillInLevel = false;
                for (let wi = 0; wi < waves.length; wi++) {
                    if (waves[wi].id === id) {
                        stillInLevel = true;
                        break;
                    }
                }
                if (!stillInLevel)
                    pruneScratch.push(id);
            }
        }
        for (let pi = 0; pi < pruneScratch.length; pi++) {
            prevAliveByWave.delete(pruneScratch[pi]);
        }
        // Hide living mesh when mecha climax owns the fantasy (optional thin residual ok)
        const showLive = !inMecha || liveCount > 0;
        const count = showLive ? liveCount : 0;
        for (const p of parts) {
            p.count = count;
        }
        for (let i = 0; i < count; i++) {
            const slot = liveScratch[i];
            const phase = slot.phase;
            // Irregular shamble: mix two sines so limbs don't mirror cleanly
            const s = Math.sin(phase);
            const s2 = Math.sin(phase * 0.53 + slot.seed * 0.02);
            const lurch = Math.abs(s) * LURCH_AMP + s2 * LURCH_AMP * 0.35;
            const legL = s * LEG_SWING + s2 * 0.12;
            const legR = -s * LEG_SWING * 0.85 + s2 * 0.08;
            // Arms hang forward and sway out of phase (zombie reach)
            const armL = 0.35 + s2 * ARM_SWAY;
            const armR = 0.4 - s * ARM_SWAY * 0.7;
            const lean = TORSO_LEAN + s2 * 0.06;
            // Face −Z toward squad (they stream down the bridge at the player)
            const yaw = Math.PI + s2 * 0.08;
            const px = slot.x;
            const pz = slot.z;
            const baseY = lurch;
            setPartMatrix(torso, i, px, baseY, pz, lean, yaw, 0);
            setPartMatrix(head, i, px, baseY, pz, lean * 1.2, yaw, s * 0.05);
            setPartMatrix(leftLeg, i, px - LEG_X, baseY + HIP_Y, pz, legL, yaw, 0);
            setPartMatrix(rightLeg, i, px + LEG_X, baseY + HIP_Y, pz, legR, yaw, 0);
            setPartMatrix(leftArm, i, px - ARM_X, baseY + SHOULDER_Y, pz, armL, yaw, 0.25);
            setPartMatrix(rightArm, i, px + ARM_X, baseY + SHOULDER_Y, pz, armR, yaw, -0.22);
        }
        for (let i = count; i < cap; i++) {
            for (const p of parts) {
                p.setMatrixAt(i, identity);
            }
        }
        for (const p of parts) {
            p.instanceMatrix.needsUpdate = true;
        }
        // Advance death collapses
        let deathCount = 0;
        for (let i = 0; i < ZOMBIE_DEATH_POOL; i++) {
            const d = deaths[i];
            if (!d.active)
                continue;
            d.age += dt > 0 ? dt : 1 / 60;
            if (d.age >= d.life) {
                d.active = false;
                continue;
            }
            const u = d.age / d.life;
            // Quick fall: pitch over + flatten
            d.rotX = u * 1.45 * d.fallDir;
            d.rotZ = u * 0.35 * d.fallDir;
            const y = Math.max(0, 0.15 * (1 - u * u));
            const sc = 1 - u * 0.85;
            dummy.position.set(d.x, y, d.z);
            dummy.rotation.set(d.rotX, 0, d.rotZ);
            dummy.scale.set(sc, sc * (1 - u * 0.5), sc);
            dummy.updateMatrix();
            deathMesh.setMatrixAt(deathCount, dummy.matrix);
            deathCount++;
        }
        deathMesh.count = deathCount;
        if (deathCount > 0)
            deathMesh.instanceMatrix.needsUpdate = true;
        deathFlesh.opacity = 0.95;
        // Advance despawn bursts
        let burstCount = 0;
        for (let i = 0; i < ZOMBIE_BURST_POOL; i++) {
            const b = bursts[i];
            if (!b.active)
                continue;
            b.age += dt > 0 ? dt : 1 / 60;
            if (b.age >= b.life) {
                b.active = false;
                continue;
            }
            const step = dt > 0 ? dt : 1 / 60;
            b.x += b.vx * step;
            b.y += b.vy * step;
            b.z += b.vz * step;
            b.vy -= 7 * step;
            const lifeT = 1 - b.age / b.life;
            const sc = 0.4 + lifeT * 0.9;
            dummy.position.set(b.x, Math.max(0.04, b.y), b.z);
            dummy.scale.setScalar(sc);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            burstMesh.setMatrixAt(burstCount, dummy.matrix);
            burstCount++;
        }
        burstMesh.count = burstCount;
        if (burstCount > 0)
            burstMesh.instanceMatrix.needsUpdate = true;
        burstMat.opacity = 0.35 + 0.45 * (burstCount > 0 ? 1 : 0);
        mesh.visible = count > 0 || deathCount > 0 || burstCount > 0;
        return count;
    };
    const setVisible = (visible) => {
        mesh.visible = visible;
    };
    const dispose = () => {
        torsoGeo.dispose();
        headGeo.dispose();
        legGeo.dispose();
        armGeo.dispose();
        deathGeo.dispose();
        burstGeo.dispose();
        fleshMat.dispose();
        limbMat.dispose();
        headMat.dispose();
        deathFlesh.dispose();
        burstMat.dispose();
        mesh.clear();
    };
    return { mesh, maxInstances: cap, apply, setVisible, dispose };
}
//# sourceMappingURL=zombies.js.map